/**
 * The food diary's layout (diet_settings.diary_layout): the order of the food sections and the
 * watch "Activity & body" block, their names, and custom sections such as Drinks or Vitamins.
 *
 * The phone app follows the same rules (android/core/engine):
 *  - A food block whose id is a meal type (breakfast ... other) is built in: logging into it saves
 *    meal_type = id, diary_section = null. Any other id is custom: meal_type 'other',
 *    diary_section = id.
 *  - A meal goes to the block matching its diary_section, else the built-in block matching its
 *    meal_type, else an "Other" group at the end. No meal is ever hidden.
 *  - A hidden block shows only on days it has entries. One activity block at most.
 *  - An empty layout means the default: breakfast, lunch, dinner, snacks, with the activity block
 *    first in wearable mode.
 */
import { MEAL_LABEL } from './labels';
import { MEAL_TYPES, type ActivityField, type DiaryBlock, type EnergyMode, type MealLog, type MealType } from './types';

export const ID_RE: RegExp = /^[a-z0-9_]{1,40}$/;
export const MAX_BLOCKS: number = 30;
export const MAX_LABEL: number = 40;
export const OTHER_GROUP_ID: string = '__other';

export const ACTIVITY_FIELDS: ReadonlyArray<{ key: ActivityField; label: string }> = [
  { key: 'active_kcal', label: 'Active calories' },
  { key: 'total_kcal', label: 'Total calories (watch)' },
  { key: 'steps', label: 'Steps' },
  { key: 'distance', label: 'Distance' },
  { key: 'exercises', label: 'Exercises and walks' },
  { key: 'heart_rate', label: 'Heart rate (avg / min / max)' },
  { key: 'resting_hr', label: 'Resting heart rate' },
  { key: 'hrv', label: 'Heart rate variability (HRV)' },
  { key: 'sleep', label: 'Sleep' },
  { key: 'spo2', label: 'Blood oxygen (SpO2)' },
  { key: 'respiratory_rate', label: 'Breathing rate' },
  { key: 'vo2max', label: 'VO2 max' },
];
const FIELD_KEYS: ReadonlySet<string> = new Set(ACTIVITY_FIELDS.map((f) => f.key));
export const DEFAULT_ACTIVITY_FIELDS: readonly ActivityField[] = ['active_kcal', 'steps', 'exercises', 'resting_hr', 'hrv', 'sleep'];

const BUILT_IN: ReadonlySet<string> = new Set<string>(MEAL_TYPES);
export const isBuiltIn = (id: string): id is MealType => BUILT_IN.has(id);

export const activityBlock = (): DiaryBlock => ({ id: 'activity', kind: 'activity', label: 'Activity & body', fields: [...DEFAULT_ACTIVITY_FIELDS] });
const food = (id: string, label: string): DiaryBlock => ({ id, kind: 'food', label });

export function defaultLayout(mode: EnergyMode): DiaryBlock[] {
  const meals: DiaryBlock[] = (['breakfast', 'lunch', 'dinner', 'snack'] as const).map((m) => food(m, MEAL_LABEL[m]));
  return mode === 'wearable' ? [activityBlock(), ...meals] : meals;
}

export const PRESETS: ReadonlyArray<{ name: string; blocks: () => DiaryBlock[] }> = [
  { name: 'Classic: breakfast, lunch, dinner, snacks', blocks: () => defaultLayout('adaptive') },
  {
    name: 'Vitamins, activity, meals, snacks, drinks',
    blocks: () => [
      food('supplements', 'Vitamins & supplements'), activityBlock(),
      food('breakfast', 'Breakfast'), food('lunch', 'Lunch'), food('dinner', 'Dinner'), food('snack', 'Snacks'), food('drinks', 'Drinks'),
    ],
  },
  {
    name: 'Training days: pre- and post-workout',
    blocks: () => [
      activityBlock(), food('breakfast', 'Breakfast'), food('lunch', 'Lunch'), food('pre_workout', 'Pre-workout'),
      food('post_workout', 'Post-workout'), food('dinner', 'Dinner'), food('snack', 'Snacks'),
    ],
  },
];

/** Cleans a stored layout: bad ids, duplicates, over-long labels and a second activity block are dropped. */
export function parseLayout(raw: unknown): DiaryBlock[] {
  if (!Array.isArray(raw)) return [];
  const out: DiaryBlock[] = [];
  const seen = new Set<string>();
  let activity: boolean = false;
  for (const x of raw) {
    if (!x || typeof x !== 'object' || out.length >= MAX_BLOCKS) continue;
    const r = x as Record<string, unknown>;
    const id: string = typeof r.id === 'string' ? r.id : '';
    const kind: unknown = r.kind;
    if (!ID_RE.test(id) || seen.has(id) || (kind !== 'food' && kind !== 'activity')) continue;
    if (kind === 'activity') { if (activity) continue; activity = true; }
    const label: string = typeof r.label === 'string' && r.label.trim() !== '' ? r.label.trim().slice(0, MAX_LABEL)
      : kind === 'activity' ? 'Activity & body' : isBuiltIn(id) ? MEAL_LABEL[id] : id;
    const block: DiaryBlock = { id, kind, label };
    if (r.hidden === true) block.hidden = true;
    if (kind === 'activity') {
      const fields = Array.isArray(r.fields) ? r.fields.filter((f): f is ActivityField => typeof f === 'string' && FIELD_KEYS.has(f)) : [];
      block.fields = fields.length > 0 ? [...new Set(fields)] : [...DEFAULT_ACTIVITY_FIELDS];
    }
    seen.add(id);
    out.push(block);
  }
  return out;
}

export function effectiveLayout(stored: ReadonlyArray<DiaryBlock>, mode: EnergyMode): DiaryBlock[] {
  const clean: DiaryBlock[] = parseLayout(stored);
  return clean.some((b) => b.kind === 'food') ? clean : defaultLayout(mode);
}

/** What a meal logged into this block is saved as. */
export function storageFor(block: Pick<DiaryBlock, 'id'>): { meal_type: MealType; diary_section: string | null } {
  return isBuiltIn(block.id) ? { meal_type: block.id, diary_section: null } : { meal_type: 'other', diary_section: block.id };
}

/** The food blocks a meal can be logged into (hidden ones left out). */
export const foodBlocks = (layout: ReadonlyArray<DiaryBlock>): DiaryBlock[] => layout.filter((b) => b.kind === 'food' && !b.hidden);

/** The block id a meal belongs to, or OTHER_GROUP_ID. */
export function blockIdFor(layout: ReadonlyArray<DiaryBlock>, meal: Pick<MealLog, 'meal_type' | 'diary_section'>): string {
  const ids = new Set(layout.filter((b) => b.kind === 'food').map((b) => b.id));
  if (meal.diary_section && ids.has(meal.diary_section)) return meal.diary_section;
  if (ids.has(meal.meal_type)) return meal.meal_type;
  return OTHER_GROUP_ID;
}

export interface DiaryGroup {
  block: DiaryBlock;
  meals: MealLog[];
}

/**
 * Blocks in layout order with their meals; hidden empty blocks dropped; meals that fit no block
 * collected in a trailing "Other" group.
 */
export function groupMeals(layout: ReadonlyArray<DiaryBlock>, meals: ReadonlyArray<MealLog>): DiaryGroup[] {
  const byBlock = new Map<string, MealLog[]>();
  for (const m of meals) {
    const id: string = blockIdFor(layout, m);
    const list: MealLog[] | undefined = byBlock.get(id);
    if (list) list.push(m); else byBlock.set(id, [m]);
  }
  const groups: DiaryGroup[] = [];
  for (const b of layout) {
    const list: MealLog[] = b.kind === 'food' ? byBlock.get(b.id) ?? [] : [];
    if (b.hidden && list.length === 0) continue;
    groups.push({ block: b, meals: list });
  }
  const other: MealLog[] | undefined = byBlock.get(OTHER_GROUP_ID);
  if (other && other.length > 0) groups.push({ block: { id: OTHER_GROUP_ID, kind: 'food', label: 'Other' }, meals: other });
  return groups;
}

/** A new custom section id from its name, unique in the layout. */
export function newSectionId(label: string, layout: ReadonlyArray<DiaryBlock>): string {
  const base: string = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 30) || 'section';
  const taken = new Set(layout.map((b) => b.id));
  // Built-in ids mean "this meal type", so a custom section never takes one.
  const reserved = (x: string): boolean => taken.has(x) || isBuiltIn(x) || x === 'activity' || x === OTHER_GROUP_ID;
  let id: string = base;
  for (let n = 2; reserved(id); n++) id = `${base}_${n}`;
  return id;
}

/** Label of the section a meal was logged under, for lists outside the diary. */
export function sectionLabel(layout: ReadonlyArray<DiaryBlock>, meal: Pick<MealLog, 'meal_type' | 'diary_section'>): string {
  const id: string = blockIdFor(layout, meal);
  return layout.find((b) => b.id === id)?.label ?? MEAL_LABEL[meal.meal_type];
}

/** The food block to open for a meal type (e.g. from the clock): its own block, else the first food block. */
export function sectionForMealType(layout: ReadonlyArray<DiaryBlock>, mealType: MealType): DiaryBlock {
  const blocks: DiaryBlock[] = foodBlocks(layout);
  return blocks.find((b) => b.id === mealType) ?? blocks[0] ?? { id: mealType, kind: 'food', label: MEAL_LABEL[mealType] };
}
